import React from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import TextInput from 'common/TextInput';
import ColorBullet from 'common/ColorBullet';
import { Grid, Col, Modal, Button, Spinner, Toast, Icon, Link } from '@tcc/crate';
import { formatNumberNumberTwoDecimalPlaces } from 'util/formatters/numberFormatter';
import { getMessage } from 'util/locale';
import { UNIT_TYPES } from 'constants/unitTypes';
import { convert } from 'util/unitConversion';
import { getLocaleUnitSymbol } from 'util/unit';
import { queryParam } from 'util/queryString';

const baseString = 'vra-application-script-rate-panel';
const classString = classNames({
  [`${baseString}`]: true
});

const validateApplicationRate = (rate) => {
  return rate && rate > 0;
};

const getValidationResult = (rate) => {
  return rate === null ? {
    isInvalid: true,
    resultedFromUserInput: true,
    helpMessages: [
      getMessage('text__application-rate--invalid-application-rate')
    ]
  }
    : {};
};

const bulletColorPurple = {
  r: 149,
  g: 39,
  b: 175
};

const bulletColorGrey = {
  r: 85,
  g: 85,
  b: 85
};

const ProductApplicationRate = ({
  applicationRate,
  selectedTrialPlacementId,
  fungicide,
  fieldId,
  onChangeProductApplicationRate,
  showConfirmModal,
  onSetShowConfirmModal,
  onConfirmDetails,
  prescriptionStatus,
  error,
  applicationArea,
  selectedDate
}) => {
  let localAreaUnit = getLocaleUnitSymbol('area');
  let localVolumeUnit = getLocaleUnitSymbol('volume');

  return (
    <section className="application-rate-display-panel">
      <section className={classString} data-test-id={baseString}>
        <header className="product-application-rate-header">
          <div className="product-application-name">{fungicide.name}</div>
          <div className="planned-application-date-container">
            <span className="planned-application-date-label">
              {getMessage('text__application-rate--application-date')}
            </span>
            <span className="planned-application-date">
              {moment.utc(selectedDate).format('MM/DD/YYYY')}
            </span>
          </div>
        </header>
        <section className="trial-type-information">
          <div className="trial-type-label">
            {getMessage('text__application-rate--trial-type-label')}
          </div>
          <div className="trial-type">
            {getMessage('text__application-rate--trial-type')}
            <Link
              className={'learn-more-link'}
              href="https://climate.app.box.com/s/77gh5gmborjip4liflhr5wgjktdtbtr5/file/850304848695"
              rel="noopener noreferrer"
              target="_blank"
            >
              <Icon className={'learn-more-icon'} glyph={Icon.set.INFO_CIRCLE} />
              {getMessage('learn-more')}
            </Link>
          </div>
        </section>
        <section className="application-rate-labels">
          <Grid className="application-rate-labels-container">
            <Col className="application-rate-zone-label" cols="6">
              {getMessage('text__application-rate--zone')}
            </Col>
            <Col className="application-rate-label product-rate-label" cols="6">
              {getMessage('text__application-rate--product-rate')}
              <div className="application-rate-unit-label">
                {`${localVolumeUnit}/${localAreaUnit}`}
              </div>
            </Col>
          </Grid>
        </section>
        <section className="">
          <Grid className="zone-application-rate">
            <Col className="application-color-bullet" cols="1">
              <ColorBullet color={bulletColorPurple} />
            </Col>
            <Col className="application-rate-label" cols="6">
              {getMessage('text__application-rate--application-area')}
              <div className="application-size-info">
                <ColorBullet color={bulletColorGrey} />
                {applicationArea.q.toFixed(1)} {applicationArea.u}
              </div>
            </Col>
            <Col className="application-rate-input" cols="5">
              <TextInput
                dataTestId="application-rate-input"
                defaultValue="0.00"
                min="0.00"
                onBlur={() => {
                  const value
                    = formatNumberNumberTwoDecimalPlaces(applicationRate);
                  if (validateApplicationRate(value)) {
                    onChangeProductApplicationRate(value);
                  } else {
                    onChangeProductApplicationRate(null);
                  }
                }}
                onChange={({ target }) => {
                  onChangeProductApplicationRate(target.value);
                }}
                placeholder="0.00"
                step="0.01"
                type="number"
                validationResult={getValidationResult(applicationRate)}
                value={applicationRate}
              />
            </Col>
          </Grid>
        </section>
        <Modal dataTestId="confirmation-modal" show={showConfirmModal} size="M">
          {!prescriptionStatus && (
            <div>
              <h1>{getMessage('experiment-confirm-modal__title')}</h1>
              <p>{getMessage('experiment-confirm-modal__body')}</p>
              <br />
              <div className="experiment-confirm-modal-button-container">
                <Button
                  className="experiment-confirm-modal-cancel-button"
                  dataTestId="close-modal-button"
                  mode="secondary"
                  onClick={() => onSetShowConfirmModal(false)}
                >
                  {getMessage('button-text__cancel')}
                </Button>
                <Button
                  className="experiment-confirm-modal-generate-button"
                  dataTestId="confirm-generate-button"
                  mode="primary"
                  onClick={() => {
                    const rate = Number.parseFloat(applicationRate);
                    onConfirmDetails({
                      productId:
                        fungicide.id,
                      fieldId,
                      selectedTrialPlacementId,
                      year: Number(queryParam('year')),
                      cropName: decodeURI(queryParam('cropName')),
                      seasonName: queryParam('seasonName'),
                      applicationRate: {
                        // Groot requires application rate in L/ha (1 gal/ac = 9.354 L/ha)
                        unit: 'liter/hectare',
                        value: localAreaUnit === 'ac' ? convert({
                          from: { q: applicationRate, u: UNIT_TYPES.gal_ac },
                          to: { u: UNIT_TYPES.l_ha }
                        }).q : rate
                      }
                    });
                  }}
                >
                  {getMessage('button-text__generate')}
                </Button>
              </div>
            </div>
          )}
          {prescriptionStatus === 'pending' && (
            <div className="experiment-confirm-modal-spinner-container">
              <Spinner className="experiment-confirm-modal-spinner" size="70" />
              <p>{getMessage('experiment-confirm-modal__pending')}</p>
            </div>
          )}
          {prescriptionStatus === 'resolved' && (
            <div className="experiment-confirm-modal-check-container">
              <Icon
                className="experiment-confirm-modal-check"
                glyph={Icon.set.CHECK}
              />
              <p>{getMessage('experiment-confirm-modal__redirect')}</p>
            </div>
          )}
        </Modal>
      </section>
      {error && (
        <Toast
          id="error-toast"
          isVisible
          text={error}
          title="Error!"
          type={Toast.WARNING}
        />
      )}
    </section>
  );
};

ProductApplicationRate.propTypes = {
  applicationArea: propTypes.number,
  applicationRate: propTypes.string,
  error: propTypes.string,
  fieldId: propTypes.string.isRequired,
  fungicide: propTypes.object.isRequired,
  onChangeProductApplicationRate: propTypes.func.isRequired,
  onConfirmDetails: propTypes.func.isRequired,
  onSetShowConfirmModal: propTypes.func.isRequired,
  prescriptionStatus: propTypes.string,
  selectedDate: propTypes.string.isRequired,
  selectedTrialPlacementId: propTypes.string.isRequired,
  showConfirmModal: propTypes.bool.isRequired
};

export default ProductApplicationRate;
